/**
 * THE VIEWER'S OWN-SPEECH DELIVERY LEDGER — what `lt.delivery.v1` leaves behind.
 *
 * One question, one slice: for the person at THIS browser, which recipients are
 * still receiving the translation of something they said? `active` opens a
 * recipient, `drained` closes it, and the count of open ones is the entire
 * output. See `lib/translation/deliveryActivity.ts` for why the signal has to
 * come from the worker, and `lib/translation/translationFlowState.ts` for what
 * the count is turned into.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHY IT IS ITS OWN SLICE AND NOT A FIELD ON `translation`
 *
 * `translationSlice` holds CALL-SCOPED, controller-authoritative facts —
 * assignments, roster, routes, generations — every one of them fenced against
 * regression and leased. This is per-UTTERANCE traffic with a lifetime measured
 * in seconds, no lease, and no meaning outside the current turn. Putting an
 * ephemeral counter inside a slice whose whole discipline is "refuse anything
 * that would rewind an authoritative snapshot" invites exactly one bug: a
 * generation fence written for assignments quietly deciding whether a status
 * light may update.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * `(epoch, seq)` ORDERS AND DEDUPES. THE TWO ARE ONE CHECK
 *
 * Per recipient, an event is admitted only if it SUPERSEDES the one on file.
 * That single rule covers both hazards the design review named:
 *
 *   REORDERING — an old `drained` arriving after a newer `active` would clear a
 *     delivery that is still running, and the light would say "they have heard
 *     you" while your sentence is still playing to them.
 *   DUPLICATES — the same event over two transports carries one `at_ms` and one
 *     `(epoch, seq)`. Equality is a repeat, counted as such and applied nowhere,
 *     so a duplicate can never be mistaken for a second delivery.
 *
 * `at_ms` is kept for telemetry and is deliberately NOT consulted here.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * THE SCOPE CHECK LIVES HERE, NOT IN THE INGEST
 *
 * `lt.delivery.v1` is addressed to the source participant, but the data channel
 * it rides is one every participant can write to, and the payload names its own
 * subject. So the reducer — not the caller — decides whether an event describes
 * THIS viewer's speech, by comparing `source_participant_id` against the
 * viewer's `stable_participant_id` supplied with the action. One enforcement
 * site, testable without a room, and it cannot be skipped by a second consumer
 * added later.
 *
 * When the viewer's own id CHANGES (a new call in the same tab, a re-issued
 * context), the ledger is dropped rather than merged. Entries minted under a
 * previous identity describe somebody else's turn; keeping them would light
 * this viewer's indicator for speech they never made.
 */

import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  compareDeliveryLineage,
  type DeliveryActivityState,
  type DeliveryActivityV1,
  type DeliveryReleaseReason,
} from '@/lib/translation/deliveryActivity';

export interface DeliveryRecipientRecord {
  state: DeliveryActivityState;
  epoch: number;
  seq: number;
  /** Telemetry only — the ordering key is `(epoch, seq)`. */
  at_ms: number;
  /** Null while active; on a drain, WHY the delivery ended. */
  release_reason: DeliveryReleaseReason | null;
}

export interface TranslationDeliveryState {
  /**
   * Whose speech the `recipients` map describes. Null until the first accepted
   * event; changing it clears the map.
   */
  sourceParticipantId: string | null;
  /** recipient stable_participant_id -> its latest accepted activity. */
  recipients: Record<string, DeliveryRecipientRecord>;
  /** Accepted events, for telemetry. Never an input to a decision. */
  accepted: number;
  /**
   * Monotonic count of accepted drains released by the worker's FAIL-OPEN
   * timeout rather than by observed playout.
   *
   * A counter and not a flag: the consumer needs to know a `stale_cap` happened
   * during THIS turn, and with two recipients the last-writer would hide one
   * behind the other. Its rate is the measurement the owner's fallback rule
   * asks for — a `stale_cap` that stops being rare is a defect in the worker's
   * drain, and this is where that shows up.
   */
  staleCapDrains: number;
  /** Closed-vocabulary refusal reasons and their counts. Telemetry only. */
  refusals: Record<string, number>;
}

const initialState: TranslationDeliveryState = {
  sourceParticipantId: null,
  recipients: {},
  accepted: 0,
  staleCapDrains: 0,
  refusals: {},
};

function refuse(state: TranslationDeliveryState, reason: string): void {
  state.refusals[reason] = (state.refusals[reason] ?? 0) + 1;
}

export interface DeliveryActivityObservation {
  /** A payload that has already passed `validateDeliveryActivityV1`. */
  event: DeliveryActivityV1;
  /** This viewer's controller-minted `stable_participant_id`, or null. */
  viewerSourceParticipantId: string | null;
}

const translationDeliverySlice = createSlice({
  name: 'translationDelivery',
  initialState,
  reducers: {
    translationDeliveryActivityObserved: (
      state,
      action: PayloadAction<DeliveryActivityObservation>,
    ) => {
      const { event, viewerSourceParticipantId } = action.payload;

      // No identity, no attribution. The alternative — accepting the event
      // because it arrived — would show another participant's delivery as this
      // viewer's own the moment two sources are in one room.
      if (viewerSourceParticipantId === null) {
        refuse(state, 'viewer_source_unknown');
        return;
      }
      if (event.source_participant_id !== viewerSourceParticipantId) {
        refuse(state, 'not_this_viewers_speech');
        return;
      }

      if (state.sourceParticipantId !== viewerSourceParticipantId) {
        state.sourceParticipantId = viewerSourceParticipantId;
        state.recipients = {};
      }

      const existing = state.recipients[event.recipient_id];
      if (existing !== undefined) {
        const order = compareDeliveryLineage(event, existing);
        if (order === 0) {
          // The same event twice — the dual-transport case the design review
          // named. Counted so the duplication rate is visible, applied nowhere.
          refuse(state, 'duplicate_lineage');
          return;
        }
        if (order < 0) {
          // The one that matters: an OLD `drained` must never clear a NEWER
          // `active`. Refusing the whole event, not merging fields, because a
          // partially applied regression is still a regression.
          refuse(state, 'stale_lineage');
          return;
        }
      }

      state.recipients[event.recipient_id] = {
        state: event.state,
        epoch: event.epoch,
        seq: event.seq,
        at_ms: event.at_ms,
        release_reason: event.state === 'drained' ? event.release_reason : null,
      };
      state.accepted += 1;
      if (event.state === 'drained' && event.release_reason === 'stale_cap') {
        state.staleCapDrains += 1;
      }
    },

    /** Drop the ledger — a call ended, or the room this viewer was in changed. */
    translationDeliveryReset: () => initialState,
  },
});

export const { translationDeliveryActivityObserved, translationDeliveryReset } =
  translationDeliverySlice.actions;

export default translationDeliverySlice.reducer;

/** The minimal root shape these selectors read, so they are testable in isolation. */
export interface TranslationDeliveryRootState {
  translationDelivery: TranslationDeliveryState;
}

const selectRecipients = (state: TranslationDeliveryRootState) =>
  state.translationDelivery.recipients;

/**
 * The recipients still receiving this viewer's translated speech.
 *
 * Memoized because it BUILDS an array: an unmemoized selector hands
 * `useSyncExternalStore` a fresh reference on every store change and re-renders
 * every consumer forever. Sorted so two reads of one ledger are the same value.
 */
export const selectOutstandingDeliveryRecipients = createSelector(
  [selectRecipients],
  (recipients): string[] =>
    Object.entries(recipients)
      .filter(([, record]) => record.state === 'active')
      .map(([recipientId]) => recipientId)
      .sort(),
);

/** How many recipients are still receiving it. The indicator's primary input. */
export const selectOutstandingDeliveryCount = (state: TranslationDeliveryRootState): number =>
  selectOutstandingDeliveryRecipients(state).length;

/** How many drains so far were the worker giving up rather than observing playout. */
export const selectStaleCapDrainCount = (state: TranslationDeliveryRootState): number =>
  state.translationDelivery.staleCapDrains;
